'use client';

import { useAuth } from '@/contexts/auth-context';
import { useOrganization } from '@/contexts/organization-context';
import { useCompanyStore } from '@/stores/company-store';
import { useFilterStore } from '@/stores/filter-store';
import { useEffect, useMemo, useState } from 'react';
import { leavesOf, TreeSelectSection, type TreeNode } from './tree-select-section';

export function OrganizationSection({
  singleStore = false,
}: {
  /** 단일 주유소만 선택 가능한 화면인지 (발행 API 가 주유소 1곳만 받는 화면들) */
  singleStore?: boolean;
} = {}) {
  // 필요한 필터 값들만 개별적으로 추출 (무한 루프 방지)
  const store = useFilterStore(state => state.store);
  const updateFilter = useFilterStore(state => state.updateFilter);
  const { selectedCompany } = useCompanyStore();
  const { getUserLevel } = useAuth();
  const {
    organizations: loadedOrganizations,
    getOrganizationsByCompany,
    getBusinessesByOrganization,
    getStoresByBusiness,
    getStoresByOrganization,
    isLoading,
  } = useOrganization();

  const userLevel = getUserLevel() || 'COMPANY';
  const isStoreLevel = userLevel === 'STORE';

  // 매장 레벨 사용자의 소속 주유소. 서버(/common/organization)가 소속 조직의 부모·자식만 돌려주므로
  // 응답에 든 STORE 전부가 소속 매장이다(관리자급은 2~3곳 겸직 가능).
  const ownStores = useMemo(
    () =>
      isStoreLevel
        ? loadedOrganizations
            .filter(org => org.type === 'STORE' && org.orgCd && org.orgName)
            .map(org => ({ value: org.orgCd!, label: org.orgName! }))
        : [],
    [loadedOrganizations, isStoreLevel]
  );

  // 소속 주유소가 1곳이면 선택기 없이 자동 선택한다 - 세금계산서 등 단일 주유소가 필수인 화면이
  // 매장 사용자에게도 동작해야 한다
  useEffect(() => {
    if (ownStores.length === 1 && (store.length !== 1 || store[0] !== ownStores[0].value)) {
      updateFilter('store', [ownStores[0].value]);
    }
  }, [ownStores, store, updateFilter]);

  // 회사 변경 시 하위 항목들 초기화
  const [previousCompany, setPreviousCompany] = useState<string>('');

  useEffect(() => {
    // 회사가 실제로 변경되었을 때만 필터를 초기화
    if (
      selectedCompany &&
      selectedCompany !== previousCompany &&
      previousCompany !== ''
    ) {
      updateFilter('organization', []);
      updateFilter('business', []);
      updateFilter('store', []);
    }
    setPreviousCompany(selectedCompany || '');
  }, [selectedCompany, updateFilter, previousCompany]);

  // 조직 > 영업지사 > 주유소 트리. 매장 레벨 사용자는 소속 주유소만 고를 수 있다.
  const tree = useMemo<TreeNode[]>(() => {
    if (isStoreLevel) {
      return ownStores.map(s => ({ value: s.value, label: s.label }));
    }
    if (!selectedCompany) return [];

    return getOrganizationsByCompany(selectedCompany)
      .map(org => {
        const businessNodes = getBusinessesByOrganization(org.orgCd!)
          .map(biz => ({
            value: biz.orgCd!,
            label: biz.orgName!,
            children: getStoresByBusiness(biz.orgCd!).map(store => ({
              value: store.orgCd!,
              label: store.orgName!,
            })),
          }))
          .filter(biz => biz.children.length > 0);

        // 영업지사를 거치지 않고 조직 직하에 달린 주유소
        const inBusiness = new Set(
          businessNodes.flatMap(biz => biz.children.map(store => store.value))
        );
        const directStores = getStoresByOrganization(org.orgCd!)
          .filter(store => !inBusiness.has(store.orgCd!))
          .map(store => ({ value: store.orgCd!, label: store.orgName! }));

        return {
          value: org.orgCd!,
          label: org.orgName!,
          children: [...businessNodes, ...directStores],
        };
      })
      .filter(org => org.children.length > 0);
  }, [
    isStoreLevel,
    ownStores,
    selectedCompany,
    getOrganizationsByCompany,
    getBusinessesByOrganization,
    getStoresByBusiness,
    getStoresByOrganization,
  ]);

  // 주유소 선택에서 조직·영업지사 필터를 되계산한다 - 하위 주유소가 모두 선택된 노드만 담는다
  const handleStoreChange = (values: string[]) => {
    updateFilter('store', values);
    if (isStoreLevel) return;

    const picked = new Set(values);
    const isFull = (node: TreeNode) =>
      leavesOf(node).every(leaf => picked.has(leaf.value));

    updateFilter(
      'organization',
      tree.filter(isFull).map(org => org.value)
    );
    updateFilter(
      'business',
      tree
        .flatMap(org => org.children ?? [])
        .filter(child => child.children?.length && isFull(child))
        .map(child => child.value)
    );
  };

  if (isLoading) {
    return <div className='text-sm text-muted-foreground'>로딩 중...</div>;
  }

  return (
    <div className='flex items-center gap-2 flex-wrap'>
      {/* 매장 레벨 사용자 - 소속 주유소 1곳이면 자동 선택(읽기 전용) */}
      {isStoreLevel && ownStores.length <= 1 ? (
        <div className='text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1'>
          {ownStores.length
            ? `주유소: ${ownStores[0].label}`
            : '소속 주유소 정보가 없습니다.'}
        </div>
      ) : (
        <TreeSelectSection
          nodes={tree}
          values={store}
          onValuesChange={handleStoreChange}
          singleSelect={singleStore}
          placeholder='주유소 선택'
        />
      )}
    </div>
  );
}
